import React, { useMemo } from 'react';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { findOptionsForSelectFilter } from '../../util/search';
import { string } from 'prop-types';
import { 
  IconCalendar, 
  IconGroupPeople, 
  IconLanguages, 
  IconPlusUser, 
  IconPointLevel 
} from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { useHourlyDuration} from '../../hooks/useHourlyDuration';

import css from './ListingPage.module.css';


const SectionDetailsMaybe = props => {
  const {
    details: { publicData },
    intl,
  } = props;

  const config = useConfiguration();

  const { groupType, peopleAtMost, languages, duration, advancedAvailabilityPlan, ability } = useMemo(
    () => publicData || {},
    [publicData]
  );

  const getAbilityLevel = ability => {
  switch (ability) {
    case 'veryEasy':
      return 1;
    case 'easy':
      return 2;
    case 'intermediate':
      return 3;
    case 'difficult':
      return 4;
    default:
      return 1;
  }
};

  const { entries } = advancedAvailabilityPlan || {};

  const hourlyDuration = useHourlyDuration(duration, entries);

  const groupTypeOptions = useMemo(
    () => findOptionsForSelectFilter('group', config.search.defaultFilters),
    [config.search.defaultFilters]
  );
  const languageOptions = useMemo(
    () => findOptionsForSelectFilter('languages', config.search.defaultFilters),
    [config.search.defaultFilters]
  );

  const { groupTypeLabelId } = useMemo(
    () => ({
      groupTypeLabelId: groupTypeOptions.find(({ key }) => key === groupType)?.label,
    }),
    [groupType, groupTypeOptions]
  );

  const selectedLanguages = useMemo(
    () =>
      languages
        ? languageOptions
            .reduce((langLabels, lang) => {
              languages.includes(lang.key) && langLabels.push(lang.label);
              return langLabels;
            }, [])
            .join(', ')
        : null,
    [languageOptions, languages]
  );

  const { groupTypeLabel, maxPeopleLabel, durationLabel, levelLabel, level } = useMemo(
    () => ({
      groupTypeLabel:
        groupTypeLabelId &&
        intl.formatMessage({
          id: groupTypeLabelId,
        }),
      maxPeopleLabel:
        peopleAtMost &&
        intl.formatMessage({ id: 'ListingPage.maxPeople' }, { people: peopleAtMost }),
      durationLabel:
        duration > 1
          ? intl.formatMessage({ id: 'ListingPage.days' }, { duration })
          : hourlyDuration
          ? intl.formatMessage({ id: 'BookingBreakdown.quantity' }, { quantity: hourlyDuration })
          : null,
      levelLabel: ability && intl.formatMessage({ id: `General.${ability}` }),
      level: getAbilityLevel(ability),
    }),
    [duration, groupTypeLabelId, hourlyDuration, intl, peopleAtMost, ability]
  );

  return (
    <div className={css.sectionDetails}>
      <h2 className={css.detailsTitle}>
        <FormattedMessage id="ListingPage.details" />
      </h2>
      <ul className={css.detailsContent}>
        <li>
          <IconCalendar className={css.sectionDetailsIcon} />
          {durationLabel}
        </li>
        <li>
          <IconPlusUser className={css.sectionDetailsIcon} />
          {maxPeopleLabel}
        </li>
        <li>
          <IconLanguages className={css.sectionDetailsIcon} />
          {selectedLanguages}
        </li>
        <li>
          <IconPointLevel 
          className={css.sectionDetailsIcon} level={level}/>
          {levelLabel}
        </li>
      </ul>
    </div>
  );
};

SectionDetailsMaybe.defaultProps = {
  rootClassName: null,
  className: null,
};

SectionDetailsMaybe.propTypes = {
  rootClassName: string,
  className: string,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(SectionDetailsMaybe);
